import React, { useState, useRef, useEffect } from 'react';
import {
    Grid,
    Button,
    OutlinedInput,
    InputLabel,
    FormControl,
    Tooltip,
    AppBar,
    Toolbar,
    IconButton,
    Snackbar,
    Alert,
} from '@mui/material';
import { ArrowUpward } from '@mui/icons-material';
import { api } from '../../api/instance';
import Loader from '../../components/Loader/Loader';

const SiteSettings = () => {
    const [settings, setSettings] = useState({});
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success',
    });
    const formRef = useRef();

    useEffect(() => {
        const getSiteSettings = async () => {
            setLoading(true);
            try {
                const response = await api.get('/settings');
                if (response.status === 200) {
                    setSettings(response.data.settings);
                }
            } catch (error) {
                console.error("Error fetching settings:", error);
                setSnackbar({
                    open: true,
                    message: 'Error loading settings',
                    severity: 'error',
                });
            } finally {
                setLoading(false);
            }
        };

        getSiteSettings();
    }, []);

    const formSubmit = async (e) => {
        if (e) e.preventDefault();
        setSubmitting(true);

        try {
            const response = await api.post("/settings", settings);

            if (response.status === 200) {
                setSnackbar({
                    open: true,
                    message: 'Settings updated successfully!',
                    severity: 'success',
                });
            }
        } catch (error) {
            console.error("Error saving settings:", error);
            setSnackbar({
                open: true,
                message: error.response?.data?.message || 'Error saving settings',
                severity: 'error',
            });
        } finally {
            setSubmitting(false);
        }
    };

    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackbar({ ...snackbar, open: false });
    };

    const scrollTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return (loading ? <Loader /> : (
        <Grid item xs={12} sm={12} md={8}>
            <form data-abide noValidate id="createForm" className="cell" onSubmit={(e) => formSubmit(e)} encType="multipart/form-data" ref={formRef}>
                <input type="text" className="is-hidden" name="createdBy" id="createdBy" aria-errormessage="dateCreatedErr" value="USERNAME" readOnly />

                <FormControl fullWidth margin="normal">
                    <InputLabel htmlFor="name">Site name</InputLabel>
                    <OutlinedInput
                        id="name"
                        name="site-name"
                        label="Site name"
                        onChange={(e) => setSettings({ ...settings, site_name: e.target.value })}
                        value={settings?.site_name || ""}
                    />
                </FormControl>

                <FormControl fullWidth margin="normal">
                    <InputLabel htmlFor="site-url">Site URL</InputLabel>
                    <OutlinedInput
                        id="url"
                        name="site-url"
                        label="Site URL"
                        onChange={(e) => setSettings({ ...settings, site_url: e.target.value })}
                        value={settings.site_url || ""}
                    />
                </FormControl>

                <FormControl fullWidth margin="normal">
                    <InputLabel htmlFor="site-email">Site email</InputLabel>
                    <OutlinedInput
                        id="email"
                        name="site-email"
                        label="Site email"
                        onChange={(e) => setSettings({ ...settings, site_email: e.target.value })}
                        value={settings.site_email || ""}
                    />
                </FormControl>

                <FormControl fullWidth margin="normal">
                    <InputLabel htmlFor="site-mobile">Site mobile</InputLabel>
                    <OutlinedInput
                        id="mobile"
                        name="site-mobile"
                        label="Site mobile"
                        onChange={(e) => setSettings({ ...settings, site_mobile: e.target.value })}
                        value={settings.site_mobile || ""}
                    />
                </FormControl>

                <FormControl fullWidth margin="normal">
                    <InputLabel htmlFor="landline">Phone landline</InputLabel>
                    <OutlinedInput
                        id="landline"
                        name="site-landline"
                        label="Phone landline"
                        onChange={(e) => setSettings({ ...settings, site_landline: e.target.value })}
                        value={settings.site_landline || ""}
                    />
                </FormControl>
            </form>

            <AppBar
                position="relative"
                className="sticky"
                color="default"
                style={{ top: 'auto', bottom: 0, borderRadius: '0.2rem', background: '#fff', marginTop: '2rem', marginBottom: '1rem' }}
            >
                <Toolbar>
                    <div style={{ flex: 2 }}>
                        <Tooltip title="Top" placement="top">
                            <IconButton onClick={scrollTop} style={{ marginLeft: -12, marginRight: 20 }} color="primary" aria-label="Menu">
                                <ArrowUpward />
                            </IconButton>
                        </Tooltip>
                    </div>
                    <div className="right" style={{ gap: '1rem', display: 'flex' }}>
                        <Button
                            fontSize="small" 
                            onClick={() => formSubmit()} 
                            style={{ flex: 1 }} 
                            color="primary" 
                            aria-label="Menu"
                            disabled={submitting}
                        >
                            {submitting ? 'Saving...' : 'Save Settings'}
                        </Button>
                    </div>
                </Toolbar>
            </AppBar>

            <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar}>
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Grid>
    ))
}

export default SiteSettings;
